/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import AppTable from 'components/appTable';
import Empty from 'components/empty';
import SearchBox from 'components/form/searchBox';
import PanelBoxLayout from 'components/layout/panelBox';
import Toolbar, { ToolbarRight } from 'components/toolbar';
import { useActions } from 'common/hooks/useActions';
import UserFilterPopup from '../filterForm';
import PaginationApp from 'components/pagination';
import StatusChip from 'components/statusChip';
import Value from 'components/value';
import { formatDateTime, MISSING } from 'common/helpers/format';

import * as S from './styles';
import * as T from 'common/types';

interface IProps {
  data: { data: []; count: number };
  loading: boolean;
}

export default function EntranceExitTimesTable({ data, loading }: IProps) {
  const { attendancesRequestAction } = useActions();
  const [meta, setMeta] = useState({ keyword: '' });
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  const fetchData = () => {
    attendancesRequestAction(meta, page, size);
  };

  useEffect(() => {
    fetchData();
  }, [meta, page, size]);

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const handleChangePageSize = (page_size: number) => {
    setSize(page_size);
  };

  return (
    <S.Content>
      <PanelBoxLayout className="flush" contentStyle={{ padding: 0 }}>
        <Toolbar>
          <SearchBox
            onChange={(e) => setMeta({ ...meta, keyword: e })}
            placeholder="Search people"
            value={meta.keyword}
          />
          <ToolbarRight>
            <UserFilterPopup setFilter={setMeta} filter={meta} />
          </ToolbarRight>
        </Toolbar>
        <AppTable
          headers={[
            { id: 1, key: 'id', title: 'ID', dir: 'left', width: 84 },
            { id: 2, key: 'emp_code', title: 'EMP Code', dir: 'left', width: 110 },
            { id: 3, key: 'name', title: 'Name', dir: 'left' },
            { id: 4, key: 'email', title: 'Email', dir: 'left' },
            { id: 5, key: 'type', title: 'Direction', dir: 'left', width: 110 },
            { id: 6, key: 'date', title: 'Date', dir: 'left', width: 156 },
            { id: 7, key: 'gate', title: 'Gate', dir: 'left', width: 110 }
          ]}
          data={data?.data}
          empty={
            meta.keyword ? (
              <Empty
                title={`No records match “${meta.keyword}”`}
                text="Check the spelling, or clear the search to see every entry and exit."
              />
            ) : (
              <Empty
                title="No entry or exit records"
                text="Nothing was recorded for the current filters. Try a different date range."
              />
            )
          }
          loading={loading}
          altanfeethiFields={{
            id: (item: T.Pages.Attendances) => (
              <S.Div className="text-left">
                <Value mono>{item?.userId}</Value>
              </S.Div>
            ),
            emp_code: (item: T.Pages.Attendances) => (
              <S.Div className="text-left">
                <Value mono fallback={MISSING.none}>
                  {item?.employeeId}
                </Value>
              </S.Div>
            ),
            /*
             * Both of these cut their value in JS — at 20 and 25 characters —
             * and glued on an ellipsis. AppTable already clips what does not
             * fit and puts the whole value on the cell's title, so the
             * hand-rolled version truncated at a fixed point regardless of the
             * column's actual width and lost the tooltip doing it.
             */
            name: (item: T.Pages.Attendances) => (
              <S.Div className="text-left captal">
                <Value fallback={MISSING.provided}>{item?.user?.displayName}</Value>
              </S.Div>
            ),
            email: (item: T.Pages.Attendances) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.provided}>{item?.user?.mail}</Value>
              </S.Div>
            ),
            /*
             * `IN` and `OU` — the out value arrives truncated to two
             * characters from the access-control system, so the column read
             * as a pair of codes rather than as a direction. The chip names
             * them; see the `attendance` map in components/statusChip.
             */
            type: (item: T.Pages.Attendances) => (
              <S.Div className="text-left">
                <StatusChip domain="attendance" value={item?.transactionType} fallback={<Value />} />
              </S.Div>
            ),
            date: (item: T.Pages.Attendances) => (
              <S.Div className="text-left">
                <Value mono>{formatDateTime(item?.transactionDate)}</Value>
              </S.Div>
            ),
            gate: (item: T.Pages.Attendances) => (
              <S.Div className="text-left">
                <Value mono fallback={MISSING.none}>
                  {item?.machineId}
                </Value>
              </S.Div>
            )
          }}
        />
      </PanelBoxLayout>
      {data?.count ? (
        <PaginationApp
          page={page}
          count={data?.count}
          handleChangePage={(page: number) => handleChangePage(page)}
          key={data?.count}
          pageSize={size}
          handleChangeTake={handleChangePageSize}
        />
      ) : (
        <></>
      )}
    </S.Content>
  );
}
